import { AlertCircle, AlertTriangle } from 'lucide-react'

import { issueSeverityLabel, issueStatusLabel, issueStatusPillClass } from '@/shared/constants/issue-status'
import type { IssueSeverity, IssueStatus } from '@/shared/constants/issue-status'

import type { LucideIcon } from 'lucide-react'

const SEV_BADGE: Record<IssueSeverity, string> = {
  critical: 'bg-gis-red-tint text-gis-red-d border-[#fecaca]',
  non_critical: 'bg-gis-amber-tint text-[#b45309] border-[#fed7aa]',
}

/** Lucide glyph per severity — replaces the old currentColor dot (matches the redesign header chips). */
const SEV_ICON: Record<IssueSeverity, LucideIcon> = { critical: AlertTriangle, non_critical: AlertCircle }

/** v2 tinted severity chip with a severity glyph. Shared by every detail drawer. */
export function SeverityChip({ severity }: { severity: IssueSeverity }) {
  const Icon = SEV_ICON[severity]
  return (
    <span className={`inline-flex items-center gap-[5px] rounded-full border px-3 py-[5px] text-[11.5px] font-bold ${SEV_BADGE[severity]}`}>
      <Icon className="h-[13px] w-[13px] stroke-[2.4]" />
      {issueSeverityLabel(severity)}
    </span>
  )
}

/**
 * The detail-drawer pill (status / age / site-status …). Defaults to the neutral white bordered chip
 * used in the issue header band. Pass `className` to override the tint (e.g. a `SITE_STATUS_PILL`
 * triple), `tabular` for day counts, `size="sm"` for dense list rows (HOD workload pill).
 */
export function DetailChip({
  children,
  className = '',
  tabular = false,
  size = 'md',
}: {
  children: React.ReactNode
  className?: string
  tabular?: boolean
  size?: 'sm' | 'md'
}) {
  const pad = size === 'sm' ? 'px-[9px] py-[3px] text-[10px]' : 'px-3 py-[5px] text-[11.5px]'
  const tint = className || 'border-gis-line bg-white text-gis-ink-2'
  return (
    <span className={`inline-flex items-center rounded-full border font-bold ${pad} ${tint}${tabular ? ' tabular-nums' : ''}`}>
      {children}
    </span>
  )
}

/** Header status chip: the canonical status tint + label with a leading status-colored dot (`bg-current`
 *  inherits the status text colour — green closed/resolved, amber pending, red rejected). Bigger sibling
 *  of `IssueStatusPill`, sized for the issue-detail header band. */
export function StatusChip({ status }: { status: IssueStatus | string }) {
  return (
    <span className={`inline-flex items-center gap-[6px] rounded-full px-3 py-[5px] text-[11.5px] font-bold ${issueStatusPillClass(status)}`}>
      <span className="h-[7px] w-[7px] rounded-full bg-current" />
      {issueStatusLabel(status)}
    </span>
  )
}

/** Compact status pill for the dense related-issue tables. Reuses the canonical status label + tint
 *  from the shared constants (single source of truth — never redefine status colours in a feature folder). */
export function IssueStatusPill({ status }: { status: IssueStatus | string }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-[2px] text-[10.5px] font-semibold whitespace-nowrap ${issueStatusPillClass(status)}`}>
      {issueStatusLabel(status)}
    </span>
  )
}
